import { Button } from "@dmca/frontend/components/button";
import {
  CellFeaturesEnum,
  type ICellJobNotification,
} from "@dmca/models/interface/cell-location";
import { serializeContactInfo } from "@dmca/utils/types";
import { Box, Stack } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import {
  JobNotificationRow,
  JobNotificationMutateModal,
  type JobNotificationMutateModalProps,
} from "@dmca/frontend/widgets/admin/notification/job-notification";
import { toast } from "react-toastify";
import type { inferInput } from "@trpc/tanstack-react-query";

export const Route = createFileRoute(
  "/_authed/_drawerLayout/admin/notification/job-notification"
)({
  component: RouteComponent,
  loader: async ({ context: { trpcQcProxy: trpc, qc } }) => {
    qc.prefetchQuery(trpc.plant.getAll.queryOptions());
    const [cells, notifications, users] = await Promise.all([
      qc.ensureQueryData(trpc.cell.getAll.queryOptions()),
      qc.ensureQueryData(trpc.cell.getJobNotification.queryOptions()),
      qc.ensureQueryData(trpc.user.getAll.queryOptions()),
    ]);
    return { cells, notifications, users };
  },
});

type DeviceIdSelection = { id: string; type: "plant" | "cell" | "all" };

function RouteComponent() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const {
    cells: initialCells,
    notifications: initialNotifications,
    users: initialUsers,
  } = Route.useLoaderData();

  const [selectedCellIds, setSelectedCellIds] = useState<DeviceIdSelection[]>(
    []
  );
  const [cellIdsForCreation, setCellIdsForCreation] = useState<
    DeviceIdSelection[]
  >([]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [selectedNotificationsForEdit, setSelectedNotificationsForEdit] =
    useState<ICellJobNotification[]>([]);
  const [openMutationModal, setOpenMutationModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  const { data: cells } = useQuery(
    trpc.cell.getAll.queryOptions(undefined, {
      select: (data) =>
        data
          .filter((x) =>
            x.featureFlag.includes(CellFeaturesEnum.JOB_NOTIFICATION)
          )
          .sort((a, b) => a.locationName.localeCompare(b.locationName)),
      initialData: initialCells,
    })
  );

  const { data: plants } = useQuery(
    trpc.plant.getAll.queryOptions(undefined, {
      select: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
    })
  );

  const { data: users } = useQuery(
    trpc.user.getAll.queryOptions(undefined, {
      select: (data) => data.sort((a, b) => a.name.localeCompare(b.name)),
      initialData: initialUsers,
    })
  );

  const { data: notifications } = useQuery(
    trpc.cell.getJobNotification.queryOptions(undefined, {
      initialData: initialNotifications,
    })
  );

  const resetPage = () => {
    setSelectedNotificationsForEdit([]);
    setCellIdsForCreation([]);
    setOpenMutationModal(false);
    setOpenCreateModal(false);
    qc.invalidateQueries(trpc.cell.getJobNotification.queryFilter());
  };

  const addMutation = useMutation(
    trpc.cell.addJobNotification.mutationOptions({
      onSuccess: () => resetPage(),
    })
  );

  const deleteMutation = useMutation(
    trpc.cell.deleteJobNotification.mutationOptions({
      onMutate: async (vars) => {
        await qc.cancelQueries(trpc.cell.getJobNotification.queryFilter());
        qc.setQueryData(
          trpc.cell.getJobNotification.queryKey(),
          (oldData) =>
            oldData?.filter((x) => !vars.some((n) => n.id === x.id)) || []
        );
      },
      onSettled: () => {
        qc.invalidateQueries(trpc.cell.getJobNotification.queryFilter());
      },
      onSuccess: () => resetPage(),
    })
  );

  const updateMutation = useMutation(
    trpc.cell.updateJobNotification.mutationOptions({
      onSuccess: () => resetPage(),
    })
  );

  const onMutationModalSubmit: (
    type: "add" | "update"
  ) => JobNotificationMutateModalProps["onSubmit"] =
    (type) => async (events, data) => {
      const { add, delete: toDelete, update } = data;
      const cellIdsToEdit =
        type === "add"
          ? cellIdsForCreation
          : selectedNotificationsForEdit.reduce((acc, item) => {
              if (item.cellId && acc.every((x) => x.id !== item.cellId)) {
                acc.push({ id: item.cellId, type: "cell" });
              } else if (
                item.plantId &&
                acc.every((x) => x.id !== item.plantId)
              ) {
                acc.push({ id: item.plantId, type: "plant" });
              } else if (
                item.allDevices &&
                acc.every((x) => x.type !== "all")
              ) {
                acc.push({ id: "", type: "all" });
              }
              return acc;
            }, [] as DeviceIdSelection[]);

      const promises: Promise<any>[] = [];

      if (type === "update") {
        const originalEvents = Array.from(
          new Set(selectedNotificationsForEdit.map((n) => n.event))
        );
        const eventsToRemove = originalEvents.filter(
          (e) => !events.includes(e)
        );
        const eventsToAdd = events.filter(
          (e) => !originalEvents.includes(e as (typeof originalEvents)[number])
        );
        const eventsToKeep = events.filter((e) =>
          originalEvents.includes(e as (typeof originalEvents)[number])
        );

        // A contact deleted in the modal may only reference one event's ID.
        // Expand to all subscriptions for that contact across the whole group.
        const contactKeysToDelete = new Set(
          toDelete.map((d) => serializeContactInfo(d.contact))
        );
        const allDeletes = Array.from(
          new Map(
            selectedNotificationsForEdit
              .filter(
                (n) =>
                  contactKeysToDelete.has(serializeContactInfo(n.contact)) ||
                  eventsToRemove.includes(n.event)
              )
              .map((n) => [n.id, { id: n.id }])
          ).values()
        );
        if (allDeletes.length > 0) {
          promises.push(deleteMutation.mutateAsync(allDeletes));
        }

        // Create subscriptions for added events for all surviving contacts + new adds
        if (eventsToAdd.length > 0) {
          const seen = new Set<string>();
          const survivingContacts = selectedNotificationsForEdit
            .filter(
              (n) => !contactKeysToDelete.has(serializeContactInfo(n.contact))
            )
            .filter((n) => {
              const key = serializeContactInfo(n.contact);
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .map((n) => update.find((u) => u.id === n.id) ?? n);

          const contactsForNewEvents = [...survivingContacts, ...add];
          if (contactsForNewEvents.length > 0) {
            const notificationsToAdd: inferInput<
              typeof trpc.cell.addJobNotification
            > = eventsToAdd.flatMap((event) =>
              cellIdsToEdit.flatMap((cellId) =>
                contactsForNewEvents.map<
                  inferInput<typeof trpc.cell.addJobNotification>[0]
                >((item) => ({
                  event,
                  contact: item.contact,
                  cell: cellId,
                }))
              )
            );
            promises.push(addMutation.mutateAsync(notificationsToAdd));
          }
        }

        // Add new contacts for all kept events
        if (add.length > 0 && eventsToKeep.length > 0) {
          const notificationsToAdd: inferInput<
            typeof trpc.cell.addJobNotification
          > = eventsToKeep.flatMap((event) =>
            cellIdsToEdit.flatMap((cellId) =>
              add.map<inferInput<typeof trpc.cell.addJobNotification>[0]>(
                (item) => ({
                  event,
                  contact: item.contact,
                  cell: cellId,
                })
              )
            )
          );
          promises.push(addMutation.mutateAsync(notificationsToAdd));
        }

        // Propagate contact info updates to ALL kept-event subscriptions for that contact
        if (update.length > 0) {
          const contactUpdates = update.flatMap((u) => {
            const originalContact = selectedNotificationsForEdit.find(
              (n) => n.id === u.id
            )?.contact;
            if (!originalContact) return [];
            const originalKey = serializeContactInfo(originalContact);
            return selectedNotificationsForEdit
              .filter(
                (n) =>
                  serializeContactInfo(n.contact) === originalKey &&
                  eventsToKeep.includes(
                    n.event as (typeof eventsToKeep)[number]
                  ) &&
                  !contactKeysToDelete.has(serializeContactInfo(n.contact))
              )
              .map((n) => ({ id: n.id, contact: u.contact }));
          });
          if (contactUpdates.length > 0) {
            promises.push(updateMutation.mutateAsync(contactUpdates));
          }
        }
      } else {
        if (toDelete.length > 0) {
          promises.push(
            deleteMutation.mutateAsync(toDelete.map((x) => ({ id: x.id })))
          );
        }

        if (add.length > 0) {
          const notificationsToAdd: inferInput<
            typeof trpc.cell.addJobNotification
          > = events.flatMap((event) =>
            cellIdsToEdit.flatMap((cellId) =>
              add.map<inferInput<typeof trpc.cell.addJobNotification>[0]>(
                (item) => ({
                  event,
                  contact: item.contact,
                  cell: cellId,
                })
              )
            )
          );
          promises.push(addMutation.mutateAsync(notificationsToAdd));
        }

        if (update.length > 0) {
          promises.push(
            updateMutation.mutateAsync(
              update.map((item) => ({ id: item.id, contact: item.contact }))
            )
          );
        }
      }

      const resp = await Promise.allSettled(promises);

      if (resp.some((x) => x.status === "rejected")) {
        toast.error("An error occurred while processing your request.");
      } else {
        toast.success(
          `Notification contacts ${
            type === "add" ? "added" : "updated"
          } successfully.`
        );
      }
    };

  return (
    <>
      <Stack sx={{ flexDirection: "column", width: "max-content" }}>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={isMultiSelect ? undefined : <CheckBoxOutlinedIcon />}
            size="small"
            variant="text"
            color={isMultiSelect ? "error" : "primary"}
            onClick={() => {
              setCellIdsForCreation([]);
              setIsMultiSelect((prev) => !prev);
            }}
            sx={{ p: 1, m: 0 }}
          >
            {isMultiSelect ? "Cancel" : "Select Multiple"}
          </Button>
          {!isMultiSelect && (
            <Button
              startIcon={
                selectedCellIds.length ===
                (cells ?? []).length + (plants ?? []).length + 1 ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )
              }
              size="small"
              variant="text"
              sx={{ p: 1, m: 0 }}
              onClick={() => {
                setSelectedCellIds((prev) =>
                  prev.length ===
                  (cells ?? []).length + (plants ?? []).length + 1
                    ? []
                    : [
                        ...(cells ?? []).map((cell) => ({
                          id: cell.cellId,
                          type: "cell" as const,
                        })),
                        ...(plants ?? []).map((plant) => ({
                          id: plant.id,
                          type: "plant" as const,
                        })),
                        { type: "all" as const, id: "" },
                      ]
                );
              }}
            >
              {selectedCellIds.length !==
              (cells ?? []).length + (plants ?? []).length + 1
                ? "Expand"
                : "Collapse"}{" "}
              All
            </Button>
          )}
          {isMultiSelect && cellIdsForCreation.length > 0 && (
            <Button
              size="small"
              variant="text"
              sx={{ p: 1, m: 0 }}
              onClick={() => setOpenCreateModal(true)}
            >
              Add Notifications
            </Button>
          )}
        </Stack>
        <JobNotificationRow
          data-testid="job-notification-row"
          label="All Cells"
          showCheckbox={isMultiSelect}
          checkboxChecked={cellIdsForCreation.some(
            (item) => item.type === "all"
          )}
          onCheckboxChange={(checked) => {
            setCellIdsForCreation((prev) =>
              checked
                ? [...prev, { type: "all" as const, id: "" }]
                : prev.filter((item) => item.type !== "all")
            );
          }}
          onAddButtonClick={() => {
            setCellIdsForCreation([{ id: "", type: "all" }]);
            setOpenCreateModal(true);
          }}
          onEditButtonClick={(notifs) => {
            setSelectedNotificationsForEdit(notifs);
            setOpenMutationModal(true);
          }}
          onDeleteButtonClick={(notifs) => {
            deleteMutation.mutate(notifs.map((x) => ({ id: x.id })));
          }}
          notifications={(notifications ?? []).filter((x) => x.allDevices)}
          expanded={
            !isMultiSelect &&
            selectedCellIds.some((item) => item.type === "all")
          }
          users={users ?? []}
          onCollapseButtonClick={() => {
            setSelectedCellIds((prev) =>
              prev.some((item) => item.type === "all")
                ? prev.filter((item) => item.type !== "all")
                : [...prev, { id: "", type: "all" as const }]
            );
          }}
        />
        {plants?.map((plant) => (
          <Box key={plant.id}>
            <JobNotificationRow
              data-testid="job-notification-row"
              label={plant.name}
              showCheckbox={isMultiSelect}
              checkboxChecked={cellIdsForCreation.some(
                (item) => item.type === "plant" && item.id === plant.id
              )}
              onCheckboxChange={(checked) => {
                setCellIdsForCreation((prev) =>
                  checked
                    ? [...prev, { type: "plant" as const, id: plant.id }]
                    : prev.filter(
                        (item) => item.type !== "plant" || item.id !== plant.id
                      )
                );
              }}
              onAddButtonClick={() => {
                setCellIdsForCreation([{ id: plant.id, type: "plant" }]);
                setOpenCreateModal(true);
              }}
              onEditButtonClick={(notifs) => {
                setSelectedNotificationsForEdit(notifs);
                setOpenMutationModal(true);
              }}
              onDeleteButtonClick={(notifs) => {
                deleteMutation.mutate(notifs.map((x) => ({ id: x.id })));
              }}
              notifications={(notifications ?? []).filter(
                (x) => x.plantId === plant.id
              )}
              expanded={
                !isMultiSelect &&
                selectedCellIds.some(
                  (item) => item.type === "plant" && item.id === plant.id
                )
              }
              users={users ?? []}
              onCollapseButtonClick={() => {
                setSelectedCellIds((prev) =>
                  prev.some(
                    (item) => item.type === "plant" && item.id === plant.id
                  )
                    ? prev.filter(
                        (item) => item.type !== "plant" || item.id !== plant.id
                      )
                    : [...prev, { id: plant.id, type: "plant" as const }]
                );
              }}
            />
            {cells
              ?.filter((cell) => cell.plantId === plant.id)
              .sort((x, y) => x.locationName.localeCompare(y.locationName))
              .map((cell) => (
                <JobNotificationRow
                  key={cell.cellId}
                  data-testid="job-notification-row"
                  showCheckbox={isMultiSelect}
                  onCheckboxChange={(checked) => {
                    setCellIdsForCreation((prev) =>
                      checked
                        ? [...prev, { id: cell.cellId, type: "cell" }]
                        : prev.filter(
                            (item) =>
                              item.type !== "cell" || item.id !== cell.cellId
                          )
                    );
                  }}
                  checkboxChecked={cellIdsForCreation.some(
                    (item) => item.type === "cell" && item.id === cell.cellId
                  )}
                  expanded={
                    !isMultiSelect &&
                    selectedCellIds.some(
                      (item) =>
                        item.type === "cell" && item.id === cell.cellId
                    )
                  }
                  onCollapseButtonClick={() => {
                    setSelectedCellIds((prev) =>
                      prev.some(
                        (item) =>
                          item.type === "cell" && item.id === cell.cellId
                      )
                        ? prev.filter(
                            (item) =>
                              item.type !== "cell" || item.id !== cell.cellId
                          )
                        : [...prev, { id: cell.cellId, type: "cell" as const }]
                    );
                  }}
                  label={cell.locationName}
                  onAddButtonClick={() => {
                    setCellIdsForCreation([{ id: cell.cellId, type: "cell" }]);
                    setOpenCreateModal(true);
                  }}
                  onEditButtonClick={(notifs) => {
                    setSelectedNotificationsForEdit(notifs);
                    setOpenMutationModal(true);
                  }}
                  onDeleteButtonClick={(notifs) => {
                    deleteMutation.mutate(notifs.map((x) => ({ id: x.id })));
                  }}
                  notifications={(notifications ?? []).filter(
                    (x) => x.cellId === cell.cellId
                  )}
                  users={users ?? []}
                />
              ))}
          </Box>
        ))}
      </Stack>

      {openMutationModal && (
        <JobNotificationMutateModal
          open={openMutationModal}
          onClose={() => setOpenMutationModal(false)}
          type="update"
          header={`Update Job Notifications for ${
            selectedNotificationsForEdit[0]?.cellId
              ? cells?.find(
                  (x) => x.cellId === selectedNotificationsForEdit[0].cellId
                )?.locationName ?? "Unknown Cell"
              : selectedNotificationsForEdit[0]?.plantId
              ? plants?.find(
                  (x) => x.id === selectedNotificationsForEdit[0].plantId
                )?.name ?? "Unknown Plant"
              : "All Cells"
          }`}
          onSubmit={onMutationModalSubmit("update")}
          notifications={selectedNotificationsForEdit}
          users={users ?? []}
          isSubmitting={
            addMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending
          }
        />
      )}
      {openCreateModal && (
        <JobNotificationMutateModal
          open={openCreateModal}
          header={`Create Job Notification for ${
            !isMultiSelect
              ? cellIdsForCreation[0]?.type === "cell"
                ? cells?.find((x) => x.cellId === cellIdsForCreation[0].id)
                    ?.locationName ?? "Unknown Cell"
                : cellIdsForCreation[0]?.type === "all"
                ? "All Cells"
                : plants?.find((x) => x.id === cellIdsForCreation[0]?.id)?.name ?? "Unknown Plant"
              : "Multiple Cells"
          }`}
          onClose={() => setOpenCreateModal(false)}
          type="add"
          onSubmit={onMutationModalSubmit("add")}
          isSubmitting={
            addMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending
          }
          users={users ?? []}
        />
      )}
    </>
  );
}
